import { useEffect, useMemo, useState } from 'react';
import {
  Achado, Marco, Plano, Reta, Serie, Zoom, br, maisPerto, useJanela,
  type Janela, type Ponto,
} from '../Grafico';
import { verTriaxialCp, type CorpoDeProva, type EnsaioTriaxial } from '../api/triaxial';

/**
 * Os gráficos do triaxial NA FICHA (onda 263).
 *
 * «Esses gráficos precisam aparecer na tela de interpretação, antes de
 * gerar o relatório» — Humberto, 03/10/2026. Até a 262 as curvas só
 * existiam no laudo PDF: quem escolhia o critério de ruptura escolhia
 * olhando tabela. Aqui as mesmas curvas do laudo — com as mesmas contas,
 * que vêm prontas do banco (`vw_triaxial_cisalhamento`,
 * `vw_triaxial_ruptura_criterios`, `vw_triaxial_envoltoria*`) — e o
 * ponto de CADA critério marcado em cima da curva.
 *
 * O gráfico não decide nada: clicar numa curva só SELECIONA a leitura
 * mais próxima; apontá-la como ruptura é um botão, como na tabela.
 */

const num = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const x = Number(v);
  return Number.isFinite(x) ? x : null;
};

/** Rótulo curto de cada critério, para caber em cima da curva. */
export const SIGLA_CRITERIO: Record<string, string> = {
  max_desviador: 'σd máx',
  max_razao_efetiva: "σ₁'/σ₃' máx",
  pico_poropressao: 'u máx',
  a_zero: 'A = 0',
  linha_kf: 'Kf',
  deformacao_limite: 'ε lim',
  inflexao: 'SH',
  manual: 'apontado',
  outro: 'apontado',
};

/** Pontos sem os nulos — a curva não inventa leitura que não existe. */
function serie(linhas: any[], fx: (l: any) => unknown, fy: (l: any) => unknown): Ponto[] {
  const s: Ponto[] = [];
  for (const l of linhas) {
    const x = num(fx(l)); const y = num(fy(l));
    if (x !== null && y !== null) s.push({ x, y });
  }
  return s;
}

/** Janela que começa no zero dos dois eixos (é como se lê um triaxial). */
function janelaDoZero(pts: Ponto[], folga = 0.08): Janela {
  if (!pts.length) return { x0: 0, x1: 1, y0: 0, y1: 1 };
  const xs = pts.map((p) => p.x); const ys = pts.map((p) => p.y);
  const x0 = Math.min(0, ...xs); const x1 = Math.max(...xs);
  const y0 = Math.min(0, ...ys); const y1 = Math.max(...ys);
  const fx = (x1 - x0) * folga || 1; const fy = (y1 - y0) * folga || 1;
  return { x0: x0 < 0 ? x0 - fx : 0, x1: x1 + fx, y0: y0 < 0 ? y0 - fy : 0, y1: y1 + fy };
}

// ══ Cisalhamento: σd × ε, u × ε, σ₁'/σ₃' × ε ══════════════════════════

/**
 * As três curvas do cisalhamento de UM corpo de prova, com o ponto que
 * cada critério escolhe marcado. O adotado sai em destaque (o mesmo
 * «achado» das outras fichas); os demais, como marcos com a sigla.
 */
export function GraficosDoCisalhamento({ cp, tipo, aoApontar, apontando }: {
  cp: CorpoDeProva;
  tipo: string;
  aoApontar(eps: number): void;
  apontando: boolean;
}) {
  const leituras = useMemo(
    () => (cp.cisalhamento ?? []).filter((c: any) => !c.descartada)
      .sort((a: any, b: any) => Number(a.deformacao_axial_pct) - Number(b.deformacao_axial_pct)),
    [cp.cisalhamento]);
  const [sel, setSel] = useState<number | null>(null);
  useEffect(() => { setSel(null); }, [cp.cp.id]);

  if (leituras.length < 2) return null;

  const eps = (l: any) => l.deformacao_axial_pct;
  const curvas = [
    { chave: 'sd', titulo: 'Tensão desviadora × deformação', eixoY: 'σd (kPa)',
      y: (l: any) => l.tensao_desvio_kpa },
    ...(tipo === 'CID' ? [] : [{
      chave: 'u', titulo: 'Poropressão × deformação', eixoY: 'u (kPa)',
      y: (l: any) => l.poropressao_kpa }]),
    ...(tipo === 'UU' ? [] : [{
      chave: 'razao', titulo: "Razão de tensões principais efetivas × deformação",
      eixoY: "σ₁'/σ₃'", y: (l: any) => l.razao_efetiva }]),
  ];

  /* Os critérios que acharam ponto, agrupados pela leitura (dois
     critérios na mesma leitura viram um marco só, com as duas siglas). */
  const porEps = new Map<number, { siglas: string[]; adotado: boolean }>();
  for (const c of cp.criterios ?? []) {
    const e = num(c.deformacao_axial_pct);
    if (e === null) continue;
    const g = porEps.get(e) ?? { siglas: [], adotado: false };
    g.siglas.push(SIGLA_CRITERIO[c.criterio] ?? c.criterio);
    g.adotado = g.adotado || !!c.adotado;
    porEps.set(e, g);
  }
  const leituraEm = (e: number) => leituras.find(
    (l: any) => Math.abs(Number(l.deformacao_axial_pct) - e) < 1e-9);
  const lSel = sel !== null ? leituras[sel] : null;

  return (
    <div className="graficos-triaxial" aria-label="Gráficos do cisalhamento">
      <div className="graficos-grade">
        {curvas.map((g) => {
          const pts = serie(leituras, eps, g.y);
          if (pts.length < 2) return null;
          const j = janelaDoZero(pts);
          /* A leitura mais perto NA TELA (frações da janela), não em
             unidades: ε vai a 15 e σd a centenas. O índice é o da
             leitura — `serie` só pula nulos, e ε e σd nunca são. */
          const conv = {
            x: { para: (v: number) => (v - j.x0) / (j.x1 - j.x0) },
            y: { para: (v: number) => (v - j.y0) / (j.y1 - j.y0) },
          };
          return (
            <figure key={g.chave} className="grafico grafico-triaxial" data-grafico={g.chave}>
              <figcaption>{g.titulo} — {cp.cp.identificacao}</figcaption>
              <Plano janela={j} tituloX="Deformação axial ε (%)"
                tituloY={g.eixoY}
                aoClicar={(p) => {
                  const k = maisPerto(p, pts, conv);
                  setSel(leituras.findIndex((l: any) =>
                    Math.abs(Number(l.deformacao_axial_pct) - pts[k].x) < 1e-9));
                }}>
                {({ px }) => (
                  <>
                    <Serie pontos={pts} px={px} marcar={false} />
                    {[...porEps.entries()].filter(([, v]) => !v.adotado).map(([e, v]) => {
                      const l = leituraEm(e); const y = l ? num(g.y(l)) : null;
                      return y === null ? null : (
                        <Marco key={e} p={{ x: e, y }} px={px} rotulo={v.siglas.join(' · ')} />
                      );
                    })}
                    {[...porEps.entries()].filter(([, v]) => v.adotado).map(([e, v]) => {
                      const l = leituraEm(e); const y = l ? num(g.y(l)) : null;
                      return y === null ? null : (
                        <Achado key={e} p={{ x: e, y }} px={px}
                          rotulo={`ruptura · ${v.siglas.join(' · ')}`} />
                      );
                    })}
                    {lSel && num(g.y(lSel)) !== null && (
                      <g className="selecionada">
                        <circle cx={px({ x: Number(lSel.deformacao_axial_pct), y: Number(g.y(lSel)) }).x}
                          cy={px({ x: Number(lSel.deformacao_axial_pct), y: Number(g.y(lSel)) }).y}
                          r={7} />
                      </g>
                    )}
                  </>
                )}
              </Plano>
            </figure>
          );
        })}
      </div>
      <p className="legenda-grafico">
        <span className="leg-achado">●</span> ruptura adotada ·{' '}
        <span className="leg-marco">○</span> ponto de outro critério ·
        {' '}clique numa curva para selecionar a leitura mais próxima.
      </p>
      {lSel && (
        <div className="leitura-selecionada" role="status">
          <span>Leitura selecionada: <b>ε = {br(Number(lSel.deformacao_axial_pct), 3)} %</b></span>
          <span>σd {br(Number(lSel.tensao_desvio_kpa), 2)} kPa</span>
          <span>u {br(Number(lSel.poropressao_kpa), 2)} kPa</span>
          {num(lSel.razao_efetiva) !== null && (
            <span>σ₁&apos;/σ₃&apos; {br(Number(lSel.razao_efetiva), 3)}</span>
          )}
          <button type="button" className="botao secundario" disabled={apontando}
            onClick={() => aoApontar(Number(lSel.deformacao_axial_pct))}>
            Apontar esta leitura como ruptura
          </button>
          <button type="button" className="discreto" onClick={() => setSel(null)}>
            limpar
          </button>
        </div>
      )}
    </div>
  );
}

// ══ Adensamento: ΔV × t ═══════════════════════════════════════════════

/**
 * A variação de volume do adensamento contra o tempo, em √t ou em log t —
 * as duas leituras em que se procura o fim do primário. O sistema não
 * traça nada (o t₅₀ continua escolha técnica, como diz a fase).
 */
export function GraficoDoAdensamento({ cp }: { cp: CorpoDeProva }) {
  const [escala, setEscala] = useState<'raiz' | 'log'>('raiz');
  const linhas = (cp.adensamento ?? []).filter((a: any) => !a.descartada);
  const pts = useMemo(() => serie(linhas, (a) => a.tempo_min, (a) => a.variacao_volume_cm3)
    .filter((p) => escala === 'raiz' ? p.x >= 0 : p.x > 0),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [cp.adensamento, escala]);
  const z = useJanela(pts, 0.08, { x: escala });
  if (pts.length < 2) return null;
  return (
    <figure className="grafico grafico-triaxial" data-grafico="adensamento">
      <figcaption>
        Variação de volume × tempo — {cp.cp.identificacao}
        <span className="alternar-escala">
          <button type="button" className={escala === 'raiz' ? 'discreto ativo' : 'discreto'}
            aria-pressed={escala === 'raiz'} onClick={() => setEscala('raiz')}>√t</button>
          <button type="button" className={escala === 'log' ? 'discreto ativo' : 'discreto'}
            aria-pressed={escala === 'log'} onClick={() => setEscala('log')}>log t</button>
        </span>
      </figcaption>
      <Zoom z={z} />
      <Plano janela={z.janela} escalaX={escala} aoJanela={z.definir}
        tituloX={escala === 'raiz' ? 'Tempo (min) — escala √t' : 'Tempo (min) — escala log'}
        tituloY="ΔV (cm³)">
        {({ px }) => <Serie pontos={pts} px={px} />}
      </Plano>
    </figure>
  );
}

// ══ Saturação: B por degrau ═══════════════════════════════════════════

export function GraficoDaSaturacao({ cp }: { cp: CorpoDeProva }) {
  const pts = serie((cp.saturacao ?? []).filter((s: any) => !s.descartado),
    (s) => s.contrapressao_kpa, (s) => s.parametro_b);
  if (pts.length < 2) return null;
  const j = janelaDoZero(pts);
  return (
    <figure className="grafico grafico-triaxial" data-grafico="saturacao">
      <figcaption>Parâmetro B × contrapressão — {cp.cp.identificacao}</figcaption>
      <Plano janela={{ ...j, y1: Math.max(1.05, j.y1) }}
        tituloX="Contrapressão (kPa)" tituloY="B = Δu/Δσ₃">
        {({ px }) => <Serie pontos={pts} px={px} />}
      </Plano>
    </figure>
  );
}

// ══ Envoltória: trajetórias p'–q (MIT e Cambridge) e Mohr ═════════════

const L_UTIL = 760 - 58 - 14;
const A_UTIL = 430 - 12 - 34;

/**
 * As trajetórias de tensões dos corpos de prova, os pontos de ruptura e a
 * envoltória — em MIT e/ou em Cambridge — e os círculos de Mohr, para
 * quem interpreta ver, ANTES do relatório, o que cada critério faz com a
 * reta. As retas por critério marcadas em «comparar» saem tracejadas por
 * cima da adotada.
 */
export function GraficosDaEnvoltoria({ d, espacoInicial }: {
  d: EnsaioTriaxial; espacoInicial: string;
}) {
  const tipo: string = d.cabecalho?.tipo ?? 'CIU';
  const efetiva = tipo !== 'UU';
  const vivos = (d.corpos ?? []).filter((c: any) => c.situacao !== 'descartado');
  const [cps, setCps] = useState<CorpoDeProva[]>([]);
  const [espaco, setEspaco] = useState<'mit' | 'cambridge' | 'ambos'>(
    (['mit', 'cambridge', 'ambos'].includes(espacoInicial) ? espacoInicial : 'mit') as any);
  const comparaveis = (d.envoltorias ?? []).filter((e: any) => e.intercepto_kpa != null);
  const [comparar, setComparar] = useState<string[]>([]);

  const chaveCps = vivos.map((c: any) => c.id).join(',');
  useEffect(() => {
    let vivo = true;
    void Promise.all(vivos.map((c: any) => verTriaxialCp(c.id)))
      .then((r) => { if (vivo) setCps(r); }).catch(() => undefined);
    return () => { vivo = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chaveCps]);

  if (!vivos.length) return null;
  const e = d.envoltoria && !d.envoltoria.motivo_sem_envoltoria ? d.envoltoria : null;
  const rupt = (d.rupturas ?? []).filter((r: any) => !r.motivo_sem_ruptura);
  const ordem = (id: string) => (vivos.findIndex((c: any) => c.id === id) % 6) + 1;

  const diagrama = (esp: 'mit' | 'cambridge') => {
    const camb = esp === 'cambridge';
    const pDe = (l: any) => (camb
      ? (efetiva ? l.p_camb_efetiva_kpa : l.p_camb_kpa)
      : (efetiva ? l.p_efetiva_kpa : l.p_kpa));
    const qDe = (l: any) => (camb ? l.q_camb_kpa : l.q_kpa);
    const trajetorias = cps.map((c) => ({
      id: c.cp.id, nome: c.cp.identificacao,
      pts: serie((c.cisalhamento ?? []).filter((l: any) => !l.descartada)
        .sort((a: any, b: any) => Number(a.deformacao_axial_pct) - Number(b.deformacao_axial_pct)),
      pDe, qDe),
    }));
    const pontosRup = rupt.map((r: any) => ({ id: r.cp_id, nome: r.identificacao,
      p: num(pDe(r)), q: num(qDe(r)) })).filter((r) => r.p !== null && r.q !== null);
    const todos = [...trajetorias.flatMap((t) => t.pts),
      ...pontosRup.map((r) => ({ x: r.p as number, y: r.q as number }))];
    const a = camb ? num(e?.intercepto_camb_kpa) : num(e?.intercepto_kpa);
    const tg = camb ? num(e?.m_camb) : num(e?.tg_alfa);
    const linha = (aa: number | null, tt: number | null) => (aa !== null && tt !== null
      ? { a: { x: 0, y: aa }, b: { x: 1, y: aa + tt } } : null);
    const adotada = linha(a, tg);
    const extras = comparaveis.filter((x: any) => comparar.includes(x.criterio));
    return (
      <figure key={esp} className="grafico grafico-triaxial" data-grafico={`trajetorias-${esp}`}>
        <figcaption>
          Trajetórias de tensões — {camb
            ? `Cambridge: p${efetiva ? "'" : ''} = (σ₁${efetiva ? "'" : ''} + 2σ₃${efetiva ? "'" : ''})/3, q = σd`
            : `MIT: p${efetiva ? "'" : ''} = (σ₁${efetiva ? "'" : ''} + σ₃${efetiva ? "'" : ''})/2, q = σd/2`}
        </figcaption>
        <Plano janela={janelaDoZero(todos)}
          tituloX={camb ? `p${efetiva ? "'" : ''} Cambridge (kPa)` : `p${efetiva ? "'" : ''} MIT (kPa)`}
          tituloY={camb ? 'q = σd (kPa)' : 'q = σd/2 (kPa)'}>
          {({ px }) => (
            <>
              {trajetorias.map((t) => (
                <g key={t.id} className={`trajetoria cp-${ordem(t.id)}`}>
                  <Serie pontos={t.pts} px={px} marcar={false} />
                </g>
              ))}
              {extras.map((x: any) => {
                const l = linha(camb ? num(x.intercepto_camb_kpa) : num(x.intercepto_kpa),
                  camb ? num(x.m_camb) : num(x.tg_alfa));
                return l && (
                  <Reta key={x.criterio} a={l.a} b={l.b} px={px} classe="reta auxiliar" />
                );
              })}
              {adotada && <Reta a={adotada.a} b={adotada.b} px={px} />}
              {pontosRup.map((r) => (
                <Achado key={r.id} p={{ x: r.p as number, y: r.q as number }} px={px} rotulo={r.nome} />
              ))}
            </>
          )}
        </Plano>
        {e && (
          <p className="legenda-grafico">
            Envoltória adotada: {camb
              ? <>M = {br(Number(e.m_camb), 4)} · φ{efetiva ? "'" : ''} = {br(Number(e.phi_camb_graus), 2)}° · c{efetiva ? "'" : ''} = {br(Number(e.coesao_camb_kpa), 2)} kPa</>
              : <>tg α = {br(Number(e.tg_alfa), 4)} · φ{efetiva ? "'" : ''} = {br(Number(e.phi_graus), 2)}° · c{efetiva ? "'" : ''} = {br(Number(e.coesao_kpa), 2)} kPa</>}
            {extras.length > 0 && ' · tracejadas: ' + extras.map((x: any) =>
              SIGLA_CRITERIO[x.criterio] ?? x.criterio).join(', ')}
          </p>
        )}
      </figure>
    );
  };

  /* Mohr: semicírculos de centro (σ₁+σ₃)/2 e raio σd/2, na base da
     envoltória (efetiva no CIU/CID, total no UU), e a reta τ = c + σ·tg φ.
     A janela tem a MESMA escala nos dois eixos — senão o círculo vira
     elipse e o ângulo da reta mente. */
  const circulos = rupt.map((r: any) => {
    const s3 = num(efetiva ? r.sigma3_efetiva_kpa : r.tensao_confinante_kpa);
    const s1 = num(efetiva ? r.sigma1_efetiva_kpa : r.sigma1_kpa);
    return s3 === null || s1 === null ? null : { id: r.cp_id, nome: r.identificacao, s3, s1 };
  }).filter(Boolean) as { id: string; nome: string; s3: number; s1: number }[];
  const sMax = Math.max(1, ...circulos.map((c) => c.s1)) * 1.08;
  const jMohr: Janela = { x0: 0, x1: sMax, y0: 0, y1: sMax * (A_UTIL / L_UTIL) };
  const phi = num(e?.phi_graus); const c0 = num(e?.coesao_kpa);

  return (
    <div className="graficos-triaxial" aria-label="Gráficos da envoltória">
      <div className="controles-grafico">
        <span className="rotulo">Diagrama p–q</span>
        {(['mit', 'cambridge', 'ambos'] as const).map((k) => (
          <button key={k} type="button" aria-pressed={espaco === k}
            className={espaco === k ? 'discreto ativo' : 'discreto'}
            onClick={() => setEspaco(k)}>
            {k === 'mit' ? 'MIT' : k === 'cambridge' ? 'Cambridge' : 'os dois'}
          </button>
        ))}
        <span className="nota">só a vista da tela — o que vai ao laudo se escolhe no campo «Diagrama p–q no laudo»</span>
      </div>
      {comparaveis.length > 1 && (
        <div className="controles-grafico">
          <span className="rotulo">Comparar com</span>
          {comparaveis.map((x: any) => (
            <label key={x.criterio} className="opcao-marcar">
              <input type="checkbox" checked={comparar.includes(x.criterio)}
                onChange={(ev) => setComparar((v) => ev.target.checked
                  ? [...v, x.criterio] : v.filter((y) => y !== x.criterio))} />
              {SIGLA_CRITERIO[x.criterio] ?? x.criterio}
            </label>
          ))}
        </div>
      )}
      <div className="graficos-grade">
        {(espaco === 'mit' || espaco === 'ambos') && diagrama('mit')}
        {(espaco === 'cambridge' || espaco === 'ambos') && diagrama('cambridge')}
        {circulos.length > 0 && (
          <figure className="grafico grafico-triaxial" data-grafico="mohr">
            <figcaption>Círculos de Mohr na ruptura — tensões {efetiva ? 'efetivas' : 'totais'}</figcaption>
            <Plano janela={jMohr} tituloX={`σ${efetiva ? "'" : ''} (kPa)`} tituloY="τ (kPa)">
              {({ px }) => (
                <>
                  {circulos.map((c) => {
                    const ctr = (c.s1 + c.s3) / 2; const r = (c.s1 - c.s3) / 2;
                    const arco: Ponto[] = Array.from({ length: 73 }, (_, i) => {
                      const t = (Math.PI * i) / 72;
                      return { x: ctr - r * Math.cos(t), y: r * Math.sin(t) };
                    });
                    return (
                      <g key={c.id} className={`trajetoria cp-${ordem(c.id)}`}>
                        <Serie pontos={arco} px={px} marcar={false} />
                      </g>
                    );
                  })}
                  {phi !== null && c0 !== null && (
                    <Reta a={{ x: 0, y: c0 }}
                      b={{ x: 1, y: c0 + Math.tan((phi * Math.PI) / 180) }} px={px} />
                  )}
                </>
              )}
            </Plano>
            {phi !== null && c0 !== null && (
              <p className="legenda-grafico">
                τ = c{efetiva ? "'" : ''} + σ{efetiva ? "'" : ''}·tg φ{efetiva ? "'" : ''}, com
                c{efetiva ? "'" : ''} = {br(c0, 2)} kPa e φ{efetiva ? "'" : ''} = {br(phi, 2)}° (a envoltória adotada, ajustada no diagrama MIT)
              </p>
            )}
          </figure>
        )}
      </div>
      <ul className="legenda-cps">
        {vivos.map((c: any) => (
          <li key={c.id} className={`cp-${ordem(c.id)}`}>
            <svg viewBox="0 0 28 8" aria-hidden="true"><line x1="0" y1="4" x2="28" y2="4" /></svg>
            {c.identificacao}
          </li>
        ))}
      </ul>
    </div>
  );
}
